import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'
import { Textarea } from '@/app/components/ui/textarea'
import { PendingTransfer, InventoryItem } from '@/app/services/inventory.service'

interface SendReviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedTransfer: PendingTransfer | null
  inventory: InventoryItem[]
  onConfirm: (
    transfer: PendingTransfer,
    status: Record<
      string,
      {
        status: 'complete' | 'partial' | 'not_sent'
        quantity: number
        reason: string
      }
    >,
  ) => void
}

export function SendReviewModal({
  open,
  onOpenChange,
  selectedTransfer,
  inventory,
  onConfirm,
}: SendReviewModalProps) {
  const [sendProductsStatus, setSendProductsStatus] = useState<
    Record<
      string,
      {
        status: 'complete' | 'partial' | 'not_sent'
        quantity: number
        reason: string
      }
    >
  >({})

  useEffect(() => {
    if (open && selectedTransfer) {
      const initialStatus: Record<
        string,
        {
          status: 'complete' | 'partial' | 'not_sent'
          quantity: number
          reason: string
        }
      > = {}
      selectedTransfer.products.forEach((product, idx) => {
        const inventoryItem = inventory.find((i) => i.name === product.name)
        const availableStock = inventoryItem?.stock || 0

        if (availableStock >= product.quantity) {
          initialStatus[idx.toString()] = {
            status: 'complete',
            quantity: product.quantity,
            reason: '',
          }
        } else if (availableStock > 0) {
          initialStatus[idx.toString()] = {
            status: 'partial',
            quantity: availableStock,
            reason: `Stock insuficiente. Disponible: ${availableStock}`,
          }
        } else {
          initialStatus[idx.toString()] = {
            status: 'not_sent',
            quantity: 0,
            reason: 'Sin stock disponible',
          }
        }
      })
      setSendProductsStatus(initialStatus)
    }
  }, [open, selectedTransfer, inventory])

  const handleConfirm = () => {
    if (selectedTransfer) {
      onConfirm(selectedTransfer, sendProductsStatus)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Revisar Solicitud de Transferencia</DialogTitle>
          <DialogDescription className="sr-only">
            Revisar y confirmar qué productos se pueden enviar
          </DialogDescription>
        </DialogHeader>
        {selectedTransfer && (
          <div className="space-y-6">
            {/* Transfer Details */}
            <div className="rounded-lg bg-slate-50 p-4">
              <h4 className="mb-3 text-sm font-medium text-slate-900">
                Detalles de la Solicitud
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">ID Transferencia:</span>
                  <span className="font-medium text-slate-900">
                    {selectedTransfer.id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Fecha:</span>
                  <span className="font-medium text-slate-900">
                    {selectedTransfer.date}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Desde:</span>
                  <span className="font-medium text-slate-900">
                    {selectedTransfer.fromBranch}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Hacia:</span>
                  <span className="font-medium text-slate-900">
                    {selectedTransfer.toBranch}
                  </span>
                </div>
              </div>
            </div>

            {/* Products List */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-900">
                Productos Solicitados
              </h4>
              <p className="text-xs text-slate-600">
                Indica qué productos puedes enviar según el stock disponible
              </p>
              <div className="space-y-3">
                {selectedTransfer.products.map((product, idx) => {
                  const status = sendProductsStatus[idx.toString()]
                  const inventoryItem = inventory.find(
                    (i) => i.name === product.name,
                  )
                  const availableStock = inventoryItem?.stock || 0

                  return (
                    <div
                      key={idx}
                      className="space-y-3 rounded-lg border border-slate-200 p-3"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {product.name}
                          </p>
                          <p className="text-xs text-slate-600">
                            Solicitado: {product.quantity} unidades
                          </p>
                          <p className="text-xs text-slate-600">
                            Stock disponible:{' '}
                            <span
                              className={
                                availableStock >= product.quantity
                                  ? 'font-medium text-green-600'
                                  : 'font-medium text-red-600'
                              }
                            >
                              {availableStock} unidades
                            </span>
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={
                            status?.status === 'complete'
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : status?.status === 'partial'
                                ? 'border-yellow-200 bg-yellow-50 text-yellow-700'
                                : 'border-red-200 bg-red-50 text-red-700'
                          }
                        >
                          {status?.status === 'complete' && 'Envío completo'}
                          {status?.status === 'partial' && 'Envío parcial'}
                          {status?.status === 'not_sent' && 'No se envía'}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        <button
                          onClick={() => {
                            setSendProductsStatus({
                              ...sendProductsStatus,
                              [idx.toString()]: {
                                status: 'complete',
                                quantity: product.quantity,
                                reason: '',
                              },
                            })
                          }}
                          disabled={availableStock < product.quantity}
                          className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${
                            status?.status === 'complete'
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : availableStock < product.quantity
                                ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          Completo
                        </button>
                        <button
                          onClick={() => {
                            setSendProductsStatus({
                              ...sendProductsStatus,
                              [idx.toString()]: {
                                status: 'partial',
                                quantity: Math.min(
                                  availableStock,
                                  product.quantity,
                                ),
                                reason: '',
                              },
                            })
                          }}
                          disabled={availableStock === 0}
                          className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${
                            status?.status === 'partial'
                              ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                              : availableStock === 0
                                ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          Parcial
                        </button>
                        <button
                          onClick={() => {
                            setSendProductsStatus({
                              ...sendProductsStatus,
                              [idx.toString()]: {
                                status: 'not_sent',
                                quantity: 0,
                                reason:
                                  availableStock === 0
                                    ? 'Sin stock disponible'
                                    : '',
                              },
                            })
                          }}
                          className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${
                            status?.status === 'not_sent'
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          No enviar
                        </button>
                      </div>

                      {status?.status === 'partial' && (
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-700">
                            Cantidad a Enviar
                          </label>
                          <Input
                            type="number"
                            min={0}
                            max={Math.min(availableStock, product.quantity)}
                            value={status.quantity}
                            onChange={(e) => {
                              const qty = parseInt(e.target.value) || 0
                              setSendProductsStatus({
                                ...sendProductsStatus,
                                [idx.toString()]: {
                                  ...status,
                                  quantity: qty,
                                },
                              })
                            }}
                            className="h-8"
                          />
                        </div>
                      )}

                      {(status?.status === 'not_sent' ||
                        status?.status === 'partial') && (
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-700">
                            Motivo
                          </label>
                          <Textarea
                            value={status.reason}
                            onChange={(e) => {
                              setSendProductsStatus({
                                ...sendProductsStatus,
                                [idx.toString()]: {
                                  ...status,
                                  reason: e.target.value,
                                },
                              })
                            }}
                            placeholder="Indica por qué no se puede enviar completo..."
                            rows={2}
                            className="text-xs"
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Summary */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="mb-2 text-sm font-medium text-blue-900">
                Resumen del Envío
              </p>
              <div className="space-y-1 text-sm text-blue-800">
                <p>
                  Productos completos:{' '}
                  {
                    Object.values(sendProductsStatus).filter(
                      (v) => v.status === 'complete',
                    ).length
                  }
                </p>
                <p>
                  Productos parciales:{' '}
                  {
                    Object.values(sendProductsStatus).filter(
                      (v) => v.status === 'partial',
                    ).length
                  }
                </p>
                {Object.values(sendProductsStatus).filter(
                  (v) => v.status === 'not_sent',
                ).length > 0 && (
                  <p className="text-red-700">
                    Productos no enviados:{' '}
                    {
                      Object.values(sendProductsStatus).filter(
                        (v) => v.status === 'not_sent',
                      ).length
                    }
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button onClick={handleConfirm} className="flex-1">
                Confirmar Envío
              </Button>
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
